/**
 * Usage:
 *
 * ```
 * const monitor = new StruggleMonitor();
 * monitor.rank(expectedWord, transcriptDelta)
 * >>> {
 *         "attemptKind": "NO_UPDATE",
 *         "rank": 0.75,
 *     }
 *
 * // Reset monitor's internal state
 * monitor.reset();
 *
 * // Check for attempt kind with enums
 * const rank = monitor.rank(expectedWord, transcriptDelta);
 * rank.attemptKind === this.Attempt.NO_UPDATE;
 * ```
 */
class StruggleMonitor {
  constructor(thresholds = {}) {
    this.Attempt = {
      SUCCEED: 'SUCCEED',
      NO_UPDATE: 'NO_UPDATE',
      LOW_EFFECT: 'LOW_EFFECT',
      PRODUCTIVE_STRUGGLE: 'PRODUCTIVE_STRUGGLE',
      UNPRODUCTIVE_STRUGGLE: 'UNPRODUCTIVE_STRUGGLE',
    };
    this.attempts = 1;
    this.thresholds = {
      LOW_EFFECT: thresholds.LOW_EFFECT || 0.1,
      MAX_PRODUCTIVE_ATTEMPTS: thresholds.MAX_PRODUCTIVE_ATTEMPTS || 10,

      // Definitions to follow. Adding them here to aid IDE
      SUCCESS: null,
      MAX_EFFORT: null,
      PRODUCTIVE_STRUGGLE: null,
      UNPRODUCTIVE_STRUGGLE: null,
    };

    this.thresholds.MAX_EFFORT = thresholds.MAX_EFFORT || (this.thresholds.MAX_PRODUCTIVE_ATTEMPTS / 2);
    this.thresholds.PRODUCTIVE_STRUGGLE = thresholds.PRODUCTIVE_STRUGGLE || [
      1 + this.thresholds.MAX_EFFORT / 2,
      this.thresholds.MAX_EFFORT,
    ];

    this.thresholds.SUCCESS = thresholds.SUCCESS || this.thresholds.MAX_PRODUCTIVE_ATTEMPTS;

    // 0.5 is the area of the smallest triangle in the setting
    this.thresholds.UNPRODUCTIVE_STRUGGLE = thresholds.UNPRODUCTIVE_STRUGGLE || [0, 0.5];
  }

  /**
   *
   * @param {Array<string>} tokens
   * @param {number} size
   * @private
   */
  _shingles(tokens, size = 1) {
    const vocab = new Set();

    for (let i = 0; i < tokens.length - size; i++) {
      vocab.add(tokens[i]);
    }

    return Array.from(vocab);
  }

  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set#implementing_basic_set_operations
  _union(setA, setB) {
    let _union = new Set(setA);
    for (let elem of setB) {
      _union.add(elem);
    }

    return _union;
  }

  _intersection(setA, setB) {
    let _intersection = new Set();
    for (let elem of setB) {
      if (setA.has(elem)) {
        _intersection.add(elem);
      }
    }

    return _intersection;
  }

  /**
   * Jaccard distance to compute distances between two sets A and B, defined as:
   * >>> cardinality(A and B) / cardinality(A or B)
   *
   * @param {Array<number>} a
   * @param {Array<number>} b
   * @private
   */
  _jaccard(a, b) {
    const setA = new Set(a);
    const setB = new Set(b);
    const intersection = this._intersection(setA, setB);
    const union = this._union(setA, setB);

    return intersection.size / union.size;
  }

  /**
   *
   * @param {string} text
   * @param {Iterable<string>} vocab
   * @private
   */
  _dummy(text, vocab) {
    return Array.from(vocab).map((v) => text.indexOf(v) >= 0);
  }

  /**
   * Fisher-Yates (aka Knuth) Shuffle
   * See https://stackoverflow.com/a/2450976/1998881
   * @param seq
   * @private
   */
  _shuffle(seq) {
    let currentIndex = seq.length;
    let randomIndex = 0;

    // While there remain elements to shuffle.
    while (currentIndex !== 0) {
      // Pick a remaining element.
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [seq[currentIndex], seq[randomIndex]] = [seq[randomIndex], seq[currentIndex]];
    }

    return seq;

  }

  /**
   * The minHash function here is defined as a random permutation of the indexes of the input shingles.
   * @param {number} vocabSize The total number of unique shingles
   * @param {number} signatureSize The number of different hash functions (permutations)
   * @private
   */
  _minHash(vocabSize, signatureSize = 10) {
    return Array.from(Array(signatureSize)).map(() => {
      const seq = Array.from(Array(vocabSize)).map((_, i) => i + 1);
      return this._shuffle(seq);
    });
  }

  /**
   *
   * @param {Array<boolean>} dummy
   * @param {Array<Array<number>>} minHash
   * @param {number} vocabSize
   * @private
   */
  _hash(dummy, minHash, vocabSize) {
    const signature = [];
    minHash.forEach((hash) => {
      hash.forEach((_, i) => {
        const idx = hash.indexOf(i + 1);
        if (dummy[idx]) {
          signature.push(idx);
        }
      });
    });
    return signature;
  }

  _intensity(expectedWord, transcriptDelta) {
    let vocab = this._shingles(`${expectedWord} ${transcriptDelta}`);
    const expected = this._dummy(expectedWord, vocab);
    const utterance = this._dummy(transcriptDelta, vocab);
    const minhash_funcs = this._minHash(vocab.length);
    const expectedSignature = this._hash(expected, minhash_funcs, vocab.length);
    const transcriptDeltaSignature = this._hash(utterance, minhash_funcs, vocab.length);

    return this._jaccard(expectedSignature, transcriptDeltaSignature);
  }

  /**
   * The definition of the filter function for convolution (f*g)
   * @param {number} intensity
   * @param {number} attempts
   * @private
   */
  _g(intensity, attempts) {
    if (intensity <= this.thresholds.LOW_EFFECT) {
      return () => this.thresholds.LOW_EFFECT;
    }

    if (intensity > this.thresholds.LOW_EFFECT && intensity < 1) {
      return (x) => x + this.thresholds.MAX_EFFORT - attempts;
    }

    if (intensity === 1) {
      return () => this.thresholds.MAX_PRODUCTIVE_ATTEMPTS;
    }
  }

  _f(attempts) {
    return () => {
      if (0 < attempts && attempts <= this.thresholds.MAX_PRODUCTIVE_ATTEMPTS) {
        return this.thresholds.MAX_PRODUCTIVE_ATTEMPTS;
      }

      return 0;
    }
  }

  /**
   * Compute the convolution f*g on the given interval
   * @param {function} f
   * @param {function} g
   * @param {Array<number>} interval
   * @private
   */
  _conv(f, g, interval) {
    const start = Math.min(f(interval[0]), g(interval[0]));
    const end = Math.min(f(interval[1]), g(interval[1]));
    const height = interval[1] - interval[0];
    return (start + end) * height / 2;
  }

  _attemptScore(intensity) {
    const f = this._f(this.attempts);
    const g = this._g(intensity, this.attempts);
    return this._conv(f, g, [this.attempts - 1, this.attempts]);
  }

  reset() {
    this.attempts = 1;
  }

  /**
   *
   * @param {string} expectedWord
   * @param {string} transcriptDelta
   * @returns {{attemptKind: string, delta: string, rank: number}}
   */
  rank(expectedWord, transcriptDelta) {
    let intensity = null;
    let attemptKind = this.Attempt.NO_UPDATE;

    if (transcriptDelta) {
      intensity = this._intensity(expectedWord, transcriptDelta);
      attemptKind = this.Attempt.LOW_EFFECT;

      if (intensity > this.thresholds.LOW_EFFECT) {
        const attemptScore = this._attemptScore(intensity);

        if (this.thresholds.PRODUCTIVE_STRUGGLE[0] <= attemptScore && attemptScore <= this.thresholds.PRODUCTIVE_STRUGGLE[1]) {
          attemptKind = this.Attempt.PRODUCTIVE_STRUGGLE;
          this.attempts += 1;
        }

        if (attemptScore === this.thresholds.SUCCESS) {
          attemptKind = this.Attempt.SUCCEED;
        }

        if (this.thresholds.UNPRODUCTIVE_STRUGGLE[0] <= attemptScore && attemptScore <= this.thresholds.UNPRODUCTIVE_STRUGGLE[1]) {
          attemptKind = this.Attempt.UNPRODUCTIVE_STRUGGLE;
          this.attempts += 1;
        }
      }
    }

    return {
      attemptKind,
      rank: intensity,
      delta: transcriptDelta,
    }
  }
}

module.exports = StruggleMonitor;
